import { Link } from "react-router-dom";
import { Bell, Clock, AlertCircle, Dumbbell, Salad, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Section, Empty, StatusBadge } from "@/components/kit";
import { HabitList } from "@/pages/coach/ClientTabsB";
import { ReminderEditor } from "@/pages/coach/ClientTabsB";
import { useAuth } from "@/context/AuthContext";
import useLoad from "@/lib/useLoad";
import api, { fmtDate } from "@/lib/api";

const ALERT_LINK = { checkin: "/app/checkin", renewal: "/app/billing", payment: "/app/billing" };

export default function ClientHome() {
  const { user } = useAuth();
  const [today] = useLoad("/me/today");
  const [habits, reloadHabits] = useLoad("/habits");
  const [plans] = useLoad("/me/plans");
  if (!today || Array.isArray(today)) return <div className="eyebrow">Loading your day…</div>;
  const toggle = async (h) => { await api.post(`/habits/${h.id}/toggle`, { date: today.local_date }); reloadHabits(); };
  const logAmount = async (habit, amount) => { await api.post(`/habits/${habit.id}/log`, { date: today.local_date, amount }); reloadHabits(); };

  return (
    <div>
      <PageHeader eyebrow={`${today.local_date} · ${today.local_time} ${today.timezone}`} title={`Hey ${user.name.split(" ")[0]}`} testid="client-home-title">
        <Button asChild><Link to="/app/logs" data-testid="home-log-workout-btn"><Dumbbell size={15} className="mr-1" />Log workout</Link></Button>
      </PageHeader>
      {today.system.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="system-alerts">
          {today.system.map((s) => (
            <Link key={s.kind} to={ALERT_LINK[s.kind]} className="flex items-center gap-3 panel border-[#FF3B30]/40 px-4 py-3 text-sm row-hover" data-testid={`alert-${s.kind}`}>
              <AlertCircle size={16} className="text-[#FF3B30]" /> {s.title}
            </Link>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Your plans" testid="home-plans">
            {!plans?.length ? <Empty text={`No active plans yet. ${today.coach_name || "Your coach"} will assign them soon.`} /> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {plans.map((p) => (
                  <Link key={p.id} to={`/app/plans?type=${p.type}`} className="panel kpi-bar p-5 row-hover" style={{ "--kpi": p.type === "workout" ? "#FF3B30" : "#10B981" }} data-testid={`home-plan-${p.type}`}>
                    {p.type === "workout" ? <Dumbbell size={18} className="text-[#FF3B30]" /> : <Salad size={18} className="text-emerald-400" />}
                    <div className="font-display text-xl font-semibold uppercase mt-2">{p.title}</div>
                    <div className="text-xs text-slate-500">{(p.content?.days || []).length} day(s)</div>
                  </Link>
                ))}
              </div>
            )}
          </Section>
          <Section title="Today's habits" testid="home-habits" action={<ClipboardCheck size={18} className="text-emerald-400" />}>
            <HabitList habits={habits} onToggle={toggle} onLog={logAmount} today={today.local_date} />
          </Section>
          <ReminderEditor tzLabel={today.timezone} />
        </div>
        <div className="space-y-6">
          <Section title="Reminders today" testid="home-reminders" action={<Bell size={18} className="text-amber-400" />}>
            {!today.reminders.length ? <Empty text="No reminders scheduled." /> : today.reminders.map((r, i) => (
              <div key={`${r.id}-${i}`} className="flex items-center justify-between py-2.5 border-t border-[#1E293B] text-sm" data-testid={`today-reminder-${r.id}`}>
                <div className="flex items-center gap-2"><Clock size={14} className="text-slate-500" />{r.title}</div>
                <span className={`font-mono-k text-xs ${r.status === "upcoming" ? "text-amber-400" : "text-slate-500"}`}>{r.today_local || r.next_local}</span>
              </div>
            ))}
          </Section>
          <Section title="Membership" testid="home-membership">
            {today.membership ? (
              <div className="text-sm space-y-2">
                <div className="font-medium">{today.membership.package_name}</div>
                <StatusBadge status={today.membership.status} />
                <div className="text-xs text-slate-500">Valid until {fmtDate(today.membership.end)}</div>
              </div>
            ) : <Empty text="No membership yet." />}
            <Button asChild variant="outline" size="sm" className="mt-4"><Link to="/app/billing">Manage</Link></Button>
          </Section>
        </div>
      </div>
    </div>
  );
}
